import { useSeo } from "@/lib/seo";
import { ARTICLES } from "@/data/articles";
import IMAGES from "@/data/images";
import { PageHeader } from "@/components/site/PageHeader";
import { Reveal } from "@/components/site/Reveal";
import { ArticleCard } from "@/components/site/ArticleCard";
import { ContextualCTA } from "@/components/site/ContextualCTA";
import { StatusBadge } from "@/components/site/StatusBadge";

const PILLARS = [
  { word: "LAND", text: "What the land is — its classification, category and what that permits." },
  { word: "LAW", text: "Who may buy, what restrictions apply, and what conversion requires." },
  { word: "LOCATION", text: "Where the land sits — authority, zone, access and planning intent." },
];

export default function LandAndLaw() {
  useSeo({
    title: "Land & Law",
    description:
      "Understanding land and law before buying in Uttarakhand — classification, purchase restrictions, conversion, circle rates and RERA, framed as considerations with sourcing noted.",
    path: "/land-and-law",
  });

  const cats = ["Land & Law", "Circle Rates", "RERA", "Due Diligence"];
  const articles = ARTICLES.filter((a) => cats.includes(a.category)).slice(0, 6);

  return (
    <>
      <PageHeader
        eyebrow="Land & Law"
        marker="SS·LAW"
        title="Read the land before you read the price"
        intro="Land classification, purchase restrictions and planning context decide what a property can be. This is where enthusiasm most often meets reality in the hills."
        breadcrumbs={[{ label: "Land & Law" }]}
      />

      <section className="bg-[#181A1B] text-[#F7F5F0] py-20 lg:py-28 my-4" data-testid="land-law-pillars">
        <div className="ss-container grid md:grid-cols-3 gap-10 lg:gap-16">
          {PILLARS.map((p, i) => (
            <Reveal key={p.word} delay={i * 0.08}>
              <div>
                <h2 className="font-display text-6xl lg:text-7xl font-light text-[#F7F5F0] leading-none">{p.word}</h2>
                <span className="mt-4 block h-px w-12 bg-[#C2A374]" />
                <p className="mt-5 text-[0.98rem] font-light text-[#F7F5F0]/70 leading-relaxed">{p.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="ss-container py-16 grid lg:grid-cols-[1fr_1fr] gap-12 lg:gap-20 items-start">
        <div>
          <div className="flex items-center gap-3 mb-5">
            <h2 className="font-display text-3xl sm:text-4xl font-light text-[#1A1C1E]">An honest caution</h2>
            <StatusBadge status="source" />
          </div>
          <p className="text-[1.08rem] leading-[1.8] font-light text-[#3a3f44] mb-5">
            Regulatory provisions, thresholds and restrictions change over time and vary by case. Selling Sunsets does not reproduce specific statutory figures as settled fact. We orient you to the right questions and point to primary sources — the Government of Uttarakhand, the revenue and registration departments, the development authorities and Uttarakhand RERA.
          </p>
          <p className="text-[1.08rem] leading-[1.8] font-light text-[#3a3f44]">
            Nothing here is legal advice. It is buyer-side orientation designed to make a complex decision legible, and to ensure you verify what matters before you commit.
          </p>
        </div>
        <Reveal className="ss-hover-grow overflow-hidden aspect-[4/5]">
          <img src={IMAGES.terrace.url} alt={IMAGES.terrace.alt} className="h-full w-full object-cover" />
        </Reveal>
      </section>

      <section className="ss-container pb-8">
        <h2 className="font-display text-3xl font-light text-[#1A1C1E] mb-10">Research on land & law</h2>
        <div className="grid md:grid-cols-3 gap-8 lg:gap-10">
          {articles.map((a, i) => <ArticleCard key={a.slug} article={a} index={i} />)}
        </div>
      </section>

      <ContextualCTA
        eyebrow="Land & Law"
        title="Considering land in Uttarakhand?"
        text="Request an independent buyer-side assessment before you commit to a specific plot — we'll help you verify what the law and the records actually say."
        whatsappMessage="Hi Suhail, I'm considering land in Uttarakhand and would like help with the land and legal due diligence."
      />
    </>
  );
}
